/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import { ACCOUNT_SETTINGS_PAGES } from '../../routeConfiguration';
import { propTypes } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import {
  AvatarLarge,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
  Favorite,
  ShoppingCartHeader,
} from '../../components';

import css from './TopbarMobileMenu.css';

const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount,
    onLogout,
    cartList,
    transactions,
  } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const user = ensureCurrentUser(currentUser);

  const userType =
    currentUser !== null && currentUser.attributes.profile.publicData.userRole !== undefined
      ? currentUser.attributes.profile.publicData.userRole
      : null;

  const userApproved =
    currentUser !== null && currentUser.attributes.profile.protectedData.approved !== undefined
      ? currentUser.attributes.profile.protectedData.approved
      : null;

  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const userId = currentUser && currentUser.id && currentUser.id.uuid;
  const createListingLinkMobile =
    userType === 'Host' && userApproved === true ? (
      <div className={css.footer}>
        <NamedLink className={css.createNewListingLink} name="NewListingPage">
          <FormattedMessage id="TopbarMobileMenu.newListingLink" />
        </NamedLink>
      </div>
    ) : null;

      const currentPageSelected = page => {
        return currentPage === page ? css.currentPageNotLogged : null;
      };

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <div className={css.root}>
        <div className={css.contentForLinks}>
          <NamedLink
            className={classNames(css.navigationLinkNotLogged, currentPageSelected('AboutPage'))}
            name="AboutPage"
          >
            <FormattedMessage id="LandingPage.toAboutPage" />
          </NamedLink>
          <NamedLink
            className={classNames(
              css.navigationLinkNotLogged,
              currentPageSelected('AboutCreatingAccountPage')
            )}
            name="AboutCreatingAccountPage"
          >
            <FormattedMessage id="LandingPage.toAboutCreatingAccountPage" />
          </NamedLink>
        </div>
        <div className={css.authenticationGreeting}>
          <FormattedMessage
            id="TopbarMobileMenu.unauthorizedGreeting"
            values={{ lineBreak: <br />, signupOrLogin }}
          />
        </div>
        {createListingLinkMobile}
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const cartCount =
    cartList && cartList.length > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={cartList.length} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const createListingLink =
    // userType === 'Host' && userApproved === true ? (
    userApproved === true ? (
      <NamedLink className={css.navigationLink} name="NewListingPage">
        <span className={css.createListing}>
          <FormattedMessage id="TopbarDesktop.createListingMobile" />
        </span>
      </NamedLink>
    ) : null;

  const manageListingsLinkMobile = (
    // userType === 'Host' ? (
    <NamedLink
      className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
      name="ManageListingsPage"
    >
      <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
    </NamedLink>
  );
  // ) : null;

  const favoritesLink = !isAuthenticatedOrJustHydrated ? null : (
    <NamedLink className={css.favoritesLink} name="FavoritesPage">
      <Favorite format="desktop" className={css.account} />
    </NamedLink>
  );

  const cartLink =
    !isAuthenticatedOrJustHydrated || !userApproved ? null : (
      <NamedLink className={css.shoppingCart} name="ShoppingCart">
        <ShoppingCartHeader format="desktop" className={css.account} cartCount={cartCount} />
      </NamedLink>
    );

  const myCalendarLink = currentUserHasListings ? (
    <NamedLink
      className={classNames(css.navigationLink, currentPageClass('MyCalendarPage'))}
      name="MyCalendarPage"
    >
      <FormattedMessage id="TopbarMobileMenu.myCalendarLink" />
    </NamedLink>
  ) : null;

  const myItineraryLink =
    transactions.length > 0 ? (
      <NamedLink
        className={classNames(css.navigationLink, currentPageClass('MyItineraryPage'))}
        name="MyItineraryPage"
      >
        <FormattedMessage id="TopbarMobileMenu.myItineraryLink" />
      </NamedLink>
    ) : null;

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>
        <NamedLink
          className={classNames(css.inbox, currentPageClass('InboxPage'))}
          name="InboxPage"
          params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
        >
          <FormattedMessage id="TopbarMobileMenu.inboxLink" />
          {notificationCountBadge}
        </NamedLink>
        {createListingLink}
        {manageListingsLinkMobile}
        {cartLink}
        {favoritesLink}
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
          name="ProfileSettingsPage"
        >
          <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
        </NamedLink>
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
          name="AccountSettingsPage"
        >
          <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
        </NamedLink>
        {myCalendarLink}
        {myItineraryLink}
        {userId ? (
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ReviewsPage'))}
            name="ReviewsPage"
            params={{ id: userId }}
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.yourReviewsLink" />
          </NamedLink>
        ) : null}
      </div>
      {createListingLinkMobile}
    </div>
  );
};

TopbarMobileMenu.defaultProps = { currentUser: null, notificationCount: 0, currentPage: null };

const { bool, func, number, string } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
};

export default TopbarMobileMenu;
